import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import { useGenericFieldsData } from 'containers/GenericFieldsHandlingLayer/context';

import SecondaryNavWrapper from '@danfloss/containers/components/SecondaryNavWrapper';
import Stepper from 'common/components/Stepper';
import ErrorMessage from 'common/components/ErrorMessage';

import { scrollToEmptyInput } from 'utils/scrollToInput';
import useSearchQuery from 'utils/useSearchQuery';
import { IFormValues } from 'common/components/FormHF/types';
import { DanflossProjectFields } from '@danfloss/containers/project-fields';
import { useTranslateFn } from 'utils/useTranslateFn';
import { TranslationKey } from '@danfloss/utils/text-translations';
import { useCommitActionHandler } from 'utils/use-commit-action-handler';
import { mapProjectFieldsToProjectDanflossCart } from '@danfloss/api/cart/iframe';
import styles from './styles.module.scss';
import { IStepView } from './types';
import { DEFAULT_ERROR_NO_MESSAGE_JUST_STOP, DEFAULT_ERROR_NO_APPROPRIATE_CONFIGURATION, VIEWS_LIST } from './constants';
import { DanFlossHeader } from '../components/DanFlossHeader';
import Loader from '../../../common/components/Loader';


const FormSwitcher = () => {
  const searchParams = useSearchQuery();
  const preselectedHoseQuery = searchParams.hose;

  const {
    order, error: loadingError, fields, isLoading,
  } = useGenericFieldsData<IFormValues<DanflossProjectFields>>();

  const isInitialLoading = !order.length && isLoading && !loadingError;
  const showUi = !isInitialLoading && !loadingError;
  const showError = !showUi && !!loadingError;

  const [viewStep, setViewStep] = useState<number>(preselectedHoseQuery ? 1 : 0);
  const [stepIndexes, setStepIndexes] = useState<Array<number>>(VIEWS_LIST.map((el, index) => index));
  const [error, setError] = useState<string | undefined>();
  const [isCartLoading, setIsCartLoading] = useState<boolean>(false);
  const navigationEl = useRef<HTMLDivElement>(null);
  const topOfApp = useRef<HTMLDivElement>(null);

  const preselectedHoseProduct = fields.hoseProduct?.value?.selected;
  const [hasProcessedPreselection, setHasProcessedPreselection] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoading && !hasProcessedPreselection) {
      setHasProcessedPreselection(true);
      if (preselectedHoseQuery) {
        if (preselectedHoseProduct) {
          setViewStep(1);
        } else {
          setViewStep(0);
        }
      }
    }
  }, [isLoading, preselectedHoseQuery, preselectedHoseProduct, hasProcessedPreselection]);

  const scrollToInput = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const navTop = topOfApp.current?.getBoundingClientRect().bottom ?? 0;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    clearTimeout(scrollToInput.current);
    scrollToInput.current = setTimeout(() => {
      // navigationEl.current!.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // navigationEl.current!.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({
        top: (navTop ?? 0) + scrollTop,
        behavior: 'smooth',
      });
    }, 300);
  }, [viewStep]);

  useEffect(() => {
    clearTimeout(scrollToInput.current);
    scrollToInput.current = setTimeout(() => {
      scrollToEmptyInput();
    }, 300);
  }, [fields]);

  const handleScrollToMakeSticky = useCallback(() => {
    const navTop = Math.round(topOfApp.current?.getBoundingClientRect().bottom ?? 0);
    if (navTop < 0) {
      navigationEl.current?.classList.add(styles.sticky);
    } else {
      navigationEl.current?.classList.remove(styles.sticky);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScrollToMakeSticky);
    return () => {
      window.removeEventListener('scroll', handleScrollToMakeSticky);
    };
  }, [handleScrollToMakeSticky]);

  const stepsSimplified = useMemo<Array<string>>(
    () => stepIndexes.map((viewIndex) => VIEWS_LIST[viewIndex].stepName),
    [stepIndexes],
  );

  const isLastStep = viewStep + 1 === VIEWS_LIST.length;

  const isNextEnabled = useMemo<boolean>(
    () => (isLastStep || !!stepIndexes.find((index) => viewStep < index)) && !error,
    [isLastStep, stepIndexes, error, viewStep],
  );

  const commitActionHandler = useCommitActionHandler(setError, setIsCartLoading, mapProjectFieldsToProjectDanflossCart);

  const t = useTranslateFn<TranslationKey>();

  const bottomLogo = t('variables.bottomLogo');

  const onNextClickHandler = useCallback(
    () => {
      if (viewStep + 1 < VIEWS_LIST.length) {
        let nextAvailableStep: IStepView | undefined;
        let index = viewStep + 1;
        const visibleViewsIndexes = stepIndexes.filter((viewIndex) => viewIndex < index);
        while (index < VIEWS_LIST.length) {
          const reqField = VIEWS_LIST[index].requiredFields;
          const isAvailableFieldExist = !!reqField.find((fieldName) => (
            !!fields[fieldName] && fields[fieldName]?.status !== 'disabled'
          ));
          if (isAvailableFieldExist || reqField.length === 0) {
            nextAvailableStep = VIEWS_LIST[index];
            visibleViewsIndexes.push(index);
            break;
          }
          index++;
        }
        const availableIndexesAfterCurrent = Array.from(
          { length: VIEWS_LIST.length - index - 1 },
          (el, ind) => ind + index + 1,
        );
        visibleViewsIndexes.push(...availableIndexesAfterCurrent);
        setStepIndexes(visibleViewsIndexes);
        if (!nextAvailableStep) {
          setError(t(DEFAULT_ERROR_NO_APPROPRIATE_CONFIGURATION));
        } else {
          setError(undefined);
          setViewStep(index);
        }
      } else {
        setIsCartLoading(true);
        // Submit to cart
        commitActionHandler();
      }
    },
    [viewStep, stepIndexes, fields, commitActionHandler],
  );

  const onBackClickHandler = useCallback(
    () => {
      const previous = stepIndexes.findIndex((viewIndex) => viewIndex === viewStep);
      if (previous - 1 >= 0) {
        setError(undefined);
        setViewStep(stepIndexes[previous - 1]);
      }
    },
    [viewStep, setViewStep, stepIndexes],
  );

  const onResetHandler = useCallback(
    () => {
      setIsCartLoading(true);
      window.location.hash = 'new';
      window.location.reload();
    },
    [setIsCartLoading],
  );

  const onStepChangeHandler = useCallback(
    (stepName: string) => {
      const step = VIEWS_LIST.findIndex((view) => view.stepName === stepName);
      if (step > viewStep) {
        return false;
      }
      setViewStep(step ?? viewStep);
      return true;
    },
    [viewStep, setViewStep],
  );

  const clearProgressError = useCallback(
    () => {
      if (error) {
        setError(undefined);
      }
    },
    [setError, error],
  );

  useEffect(() => {
    // Dropdowns are only updated normally from server, so need to re-evaluate "next" button
    console.log('error is reset on server updating fields');
    clearProgressError();
  }, [fields]);

  const handleSetNoFieldsAvailable = useCallback(
    () => setError(t(DEFAULT_ERROR_NO_MESSAGE_JUST_STOP)),
    [setError],
  );

  const ViewComponent = VIEWS_LIST[viewStep].component;

  return (
    <div className={styles.wrapper}>
      {showUi && (
        <>
          <div ref={topOfApp} className={styles.top}>
            <DanFlossHeader />
          </div>
          <div
            ref={navigationEl}
            className={styles.navigation}
          >
            <Stepper
              onClickStepper={onStepChangeHandler}
              steps={stepsSimplified}
              step={VIEWS_LIST[viewStep].stepName}
            />
            <SecondaryNavWrapper />
          </div>

          <div className={styles.container}>
            <ViewComponent
              title={t(VIEWS_LIST[viewStep].title)}
              description={t(VIEWS_LIST[viewStep].description)}
              key={VIEWS_LIST[viewStep].title}
              onNextClickHandler={onNextClickHandler}
              onBackClickHandler={onBackClickHandler}
              onStepChange={onStepChangeHandler}
              onResetHandler={onResetHandler}
              fieldsNames={VIEWS_LIST[viewStep].requiredFields}
              isNextDisabled={!isNextEnabled}
              clearProgressError={clearProgressError}
              isLoading={isCartLoading}
              handleSetNoFieldsAvailable={handleSetNoFieldsAvailable}
            >
              {error && (
                <ErrorMessage
                  text={error}
                  className={styles.commonError}
                />
              )}
            </ViewComponent>

            <div className={styles.bottomLogo}>
              <img src={bottomLogo} alt="Logo" className={styles.bottomLogo} />
              <a
                href={t('texts.footer.link')}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.bottomText}
              >
                {t('texts.footer.text')}
              </a>
            </div>
          </div>
        </>
      )}
      {isInitialLoading && <Loader middle className={styles.loading} /> }
      { showError && (
        <ErrorMessage
          text={loadingError.statusText || 'Not Found'}
          className={styles.commonError}
        />
      )}
      <div id="modal-root" />
    </div>
  );
};

export default FormSwitcher;
